import Vue from "vue";
import Router from "vue-router";
import Home from "./views/Home.vue";
import ValidationsList from "./views/ValidationsList";
import UserManagementList from "./views/UserManagementList";
import CustomerDetails from "./views/CustomerDetails";
import Vehicles from "./views/Vehicles";
import Companies from "./views/Companies";
import Fleets from "./views/Fleets";
import Drivers from "./views/Drivers";
import Bookings from "./views/Bookings";
import Trips from "./views/Trips";
import Dispatching from "./views/Dispatching";
import Geo from "./views/Geo";
import Pricing from "./views/Pricing";
import Invoicing from "./views/Invoicing";
import InvoicingRules from "./views/InvoicingRules";
import Scheduler from "./views/Scheduler";
import PurchaseInvoicing from "./views/PurchaseInvoicing";
import SalesInvoicing from "./views/SalesInvoicing";
import MissingVerifoneEvents from "./views/MissingVerifoneEvents";
import PaytrailTransactions from "./views/PaytrailTransactions";
import ProvisionBatchMonitoring from "./views/ProvisionBatchMonitoring";
import KelaBatches from "./views/KelaBatches";
import Processes from "./views/Processes";
import PaymentManagementAvailability from "./views/availability/PaymentManagementAvailability";
import AuthCallback from "./views/AuthCallback";
import Unauthorized from "./views/Unauthorized";

Vue.use(Router);

const router = new Router({
    mode: "history",
    routes: [
        {
            path: "/",
            name: "Home",
            component: Home
        },
        {
            path: "/validations",
            name: "Validations",
            component: ValidationsList,
            meta: { authorize: { role: "validation" } }
        },
        {
            path: "/pricing",
            name: "Pricing",
            component: Pricing,
            meta: { authorize: { role: "pricing" } }
        },
        {
            path: "/invoicing",
            name: "Invoicing",
            component: Invoicing,
            meta: { authorize: { role: "paymentmanagement" } }
        },
        {
            path: "/invoicing/rules",
            name: "InvoicingRules",
            component: InvoicingRules,
            meta: { authorize: { role: "paymentmanagement" } }
        },
        {
            path: "/invoicing/salesinvoicing",
            name: "SalesInvoicing",
            component: SalesInvoicing,
            meta: { authorize: { role: "paymentmanagement" } }
        },
        {
            path: "/invoicing/purchaseinvoicing",
            name: "PurchaseInvoicing",
            component: PurchaseInvoicing,
            meta: { authorize: { role: "paymentmanagement" } }
        },
        {
            path: "/invoicing/verifone",
            name: "MissingVerifoneEvents",
            component: MissingVerifoneEvents,
            meta: { authorize: { role: "paymentmanagement" } }
        },
        {
            path: "/invoicing/paytrail",
            name: "PaytrailTransactions",
            component: PaytrailTransactions,
            meta: { authorize: { role: "paymentmanagement" } }
        },
        {
            path: "/invoicing/provision-batches",
            name: "ProvisionBatchMonitoring",
            component: ProvisionBatchMonitoring,
            meta: { authorize: { role: "paymentmanagement" } }
        },
        {
            path: "/invoicing/kela-batches",
            name: "KelaBatches",
            component: KelaBatches,
            meta: { authorize: { role: "paymentmanagement" } }
        },
        {
            path: "/scheduler",
            name: "Scheduler",
            component: Scheduler,
            meta: { authorize: { role: "paymentmanagement" } }
        },
        {
            path: "/processes",
            name: "Processes",
            component: Processes,
            meta: {
                authorize: { role: "paymentmanagement" },
                allowedEmails: [
                    "olli.elolahde@taksikuutio.fi",
                    "mihail.tommonen@taksikuutio.fi",
                    "kaapo.mikkonen@taksikuutio.fi"
                ]
            }
        },
        {
            path: "/user-management",
            name: "User Management",
            component: UserManagementList,
            meta: { authorize: { role: "usermanagement" } }
        },
        {
            path: "/customers/:id",
            name: "CustomerDetails",
            component: CustomerDetails,
            meta: { authorize: { role: "usermanagement" } }
        },
        {
            path: "/geo",
            name: "GeoService",
            component: Geo,
            meta: { authorize: { role: "geo" } }
        },
        {
            path: "/vehicles",
            name: "Vehicles",
            component: Vehicles,
            meta: { authorize: { role: "vehicle" } }
        },
        {
            path: "/companies",
            name: "Companies",
            component: Companies
        },
        {
            path: "/fleets",
            name: "Fleets",
            component: Fleets
        },
        {
            path: "/drivers",
            name: "Drivers",
            component: Drivers
        },
        {
            path: "/bookings",
            name: "Bookings",
            component: Bookings
        },
        {
            path: "/trips",
            name: "Trips",
            component: Trips
        },
        {
            path: "/dispatching",
            name: "Dispatching",
            component: Dispatching
        },
        {
            path: "/availability/payment-management",
            name: "PaymentManagement availability",
            component: PaymentManagementAvailability
        },
        {
            path: "/auth/callback",
            name: "AuthCallback",
            component: AuthCallback,
            meta: { skipAuthCheck: true }
        },
        {
            path: "/unauthorized",
            name: "Unauthorized",
            component: Unauthorized,
            meta: { skipAuthCheck: true }
        }
    ]
});

export default router;

router.beforeEach(async(to, from, next) => {
    // Skip auth check for certain routes
    if (to.meta.skipAuthCheck) {
        return next();
    }

    const { authorize, allowedEmails } = to.meta;

    // If route doesn't require authorization, allow access
    if (!authorize) {
        return next();
    }

    try {
        // Initialize auth service if not already done
        const authService = Vue.prototype.$AuthService;
        if (!authService.isInitialized) {
            await authService.initialize();
        }

        // Check if user is authenticated
        const isAuthenticated = await authService.isAuthenticated();

        if (!isAuthenticated) {
            // Store intended destination for post-login redirect
            sessionStorage.setItem("auth.redirectPath", to.fullPath);

            // Redirect to sign in (this will handle the redirect flow)
            await authService.signIn();
            return; // Don't call next(), redirect handles navigation
        }

        // Check role-based authorization
        const canAccess = authService.canAccessSite(authorize.role);

        if (!canAccess) {
            return next({ name: "Unauthorized" });
        }

        // Check email-based allowlist if defined
        if (allowedEmails && allowedEmails.length > 0) {
            const account = authService.getActiveAccount();
            const userEmail = account && account.username ? account.username.toLowerCase() : "";
            const isAllowed = allowedEmails.some(e => e.toLowerCase() === userEmail);
            if (!isAllowed) {
                return next({ name: "Unauthorized" });
            }
        }

        // User is authenticated and authorized
        next();
    } catch (error) {
        // Router guard error - redirect to unauthorized page
        next({ name: "Unauthorized" });
    }
});
